/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import {
  Table,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Button,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Breadcrumb,
} from "antd";
import { AiOutlineUpload } from "react-icons/ai";
import { Link } from "react-router-dom";

import {
  getHeroBanners,
  createHeroBanner,
  updateHeroBanner,
  deleteHeroBanner,
  uploadFile,
} from "../../../services/cms";

const { Option } = Select;

const HeroBanners = () => {
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaTypeStored, setMediaTypeStored] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getHeroBanners();
      setRows(data.banners || []);
    } catch (e) {
      message.error("Failed to load hero banners");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setMediaFile(null);
    setMediaUrl("");
    setMediaTypeStored("");
    form.resetFields();
    form.setFieldsValue({ type: "image", order: 0, isActive: true });
    setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r._id);
    setMediaFile(null);
    setMediaUrl((r.media && r.media.url) || "");
    setMediaTypeStored((r.media && r.media.type) || "");
    form.setFieldsValue({
      type: r.type || "image",
      caption: r.caption || "",
      subCaption: r.subCaption || "",
      ctaText: r.ctaText || "",
      ctaLink: r.ctaLink || "",
      order: r.order || 0,
      isActive: r.isActive !== false,
    });
    setModalOpen(true);
  };

  const onSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }
    setSaving(true);
    try {
      let url = mediaUrl;
      let mediaType = mediaTypeStored;
      if (mediaFile) {
        const up = await uploadFile(mediaFile);
        url = up.url;
        mediaType = up.type;
      }
      if (!url) {
        message.warning("Please select an image or video");
        setSaving(false);
        return;
      }
      const payload = {
        type: values.type,
        media: { url, type: mediaType },
        caption: values.caption || "",
        subCaption: values.subCaption || "",
        ctaText: values.ctaText || "",
        ctaLink: values.ctaLink || "",
        order: values.order || 0,
        isActive: values.isActive !== false,
      };
      if (editingId) await updateHeroBanner(editingId, payload);
      else await createHeroBanner(payload);
      message.success(`Hero banner ${editingId ? "updated" : "created"}`);
      setModalOpen(false);
      load();
    } catch (e) {
      message.error(
        (e &&
          e.response &&
          e.response.data &&
          e.response.data.error &&
          e.response.data.error.message) ||
          "Save failed"
      );
    }
    setSaving(false);
  };

  const onDelete = async (id) => {
    try {
      await deleteHeroBanner(id);
      message.success("Hero banner deleted");
      load();
    } catch (e) {
      message.error("Delete failed");
    }
  };

  const columns = [
    {
      title: "Preview",
      dataIndex: "media",
      render: (m, r) =>
        m && m.url ? (
          r.type === "video" ? (
            <span>🎬 video</span>
          ) : (
            <img src={m.url} alt="" style={{ height: 40 }} />
          )
        ) : (
          "—"
        ),
    },
    { title: "Caption", dataIndex: "caption" },
    { title: "Type", dataIndex: "type" },
    { title: "Order", dataIndex: "order" },
    {
      title: "Active",
      dataIndex: "isActive",
      render: (a) => (a === false ? "No" : "Yes"),
    },
    {
      title: "Actions",
      render: (_, r) => (
        <div className="flex gap-4">
          <a onClick={() => openEdit(r)}>Edit</a>
          <Popconfirm title="Delete this banner?" onConfirm={() => onDelete(r._id)}>
            <a style={{ color: "#e3342f" }}>Delete</a>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="content-panel px-4">
      <div className="mx-4">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <Link to="/dashboard">Home</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Hero Banner</Breadcrumb.Item>
        </Breadcrumb>
        <div className="flex justify-between">
          <h1 className="page-heading">Hero Banner</h1>
          <button
            onClick={openAdd}
            className="bg-[#FA6210] h-12 px-5 font-semibold hover:bg-[#15538B] rounded-md text-white"
          >
            Add Banner
          </button>
        </div>
        <div className="border-b -mt-4 mb-8" />
        <Table
          rowKey="_id"
          loading={loading}
          columns={columns}
          dataSource={rows}
        />
        <p className="text-gray-500 text-sm mt-2">
          The home page shows the active banner. If none is active, the original
          hero is shown.
        </p>
      </div>

      <Modal
        title={editingId ? "Edit Hero Banner" : "Add Hero Banner"}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        confirmLoading={saving}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="type" label="Media type">
            <Select>
              <Option value="image">Image</Option>
              <Option value="video">Video (autoplay, muted, loop)</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Media (image or video)">
            {mediaUrl ? (
              <div className="mb-2 text-xs text-gray-500 break-all">
                Current: {mediaUrl}
              </div>
            ) : null}
            <Upload
              beforeUpload={(f) => {
                setMediaFile(f);
                return false;
              }}
              maxCount={1}
              accept="image/*,video/mp4"
            >
              <Button icon={<AiOutlineUpload />}>Select file</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="caption" label="Caption (optional)">
            <Input />
          </Form.Item>
          <Form.Item name="subCaption" label="Sub-caption (optional)">
            <Input />
          </Form.Item>
          <Form.Item name="ctaText" label="Button text (optional)">
            <Input />
          </Form.Item>
          <Form.Item name="ctaLink" label="Button link (optional)">
            <Input placeholder="/register or https://..." />
          </Form.Item>
          <Form.Item name="order" label="Order">
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HeroBanners;
